import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Car, Gauge, Calendar, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatMileage = (mileage) => {
  return new Intl.NumberFormat('en-KE').format(mileage) + ' km';
};

export default function AutoPage() {
  const { token } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleType, setVehicleType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchVehicles();
  }, [vehicleType]);

  const fetchVehicles = async () => {
    try {
      const params = vehicleType !== 'all' ? `?vehicle_type=${vehicleType}` : '';
      const response = await axios.get(`${API_URL}/api/vehicles${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error('Vehicles fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles.filter(v => 
    v.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.model.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout title="Jambo Auto" showSearch>
      <div className="p-4 lg:p-6">
        {/* Hero Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-64">
          <img 
            src="https://images.unsplash.com/photo-1763268265028-1631360c07dd?w=1200"
            alt="Jambo Auto"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
            <div className="p-6 lg:p-10">
              <h2 className="text-2xl lg:text-4xl font-bold text-white mb-2">Drive Your Dream</h2>
              <p className="text-white/80 text-sm lg:text-base">Buy, lease, or subscribe to premium vehicles</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search by make or model..." 
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="vehicle-search"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={vehicleType} onValueChange={setVehicleType} className="mb-6">
          <TabsList className="w-full max-w-sm">
            <TabsTrigger value="all" className="flex-1" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="buy" className="flex-1" data-testid="tab-buy">Buy</TabsTrigger>
            <TabsTrigger value="lease" className="flex-1" data-testid="tab-lease">Lease</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Vehicles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48" />
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-6 w-1/3" />
                </CardContent>
              </Card>
            ))
          ) : filteredVehicles.length ? (
            filteredVehicles.map((vehicle) => (
              <Card 
                key={vehicle.id} 
                className="overflow-hidden card-hover group"
                data-testid={`vehicle-card-${vehicle.id}`}
              >
                <div className="h-48 overflow-hidden bg-muted relative">
                  <img 
                    src={vehicle.image_url || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=600'}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      vehicle.vehicle_type === 'buy' 
                        ? 'bg-primary text-white' 
                        : 'bg-purple-500 text-white'
                    }`}>
                      {vehicle.vehicle_type === 'buy' ? 'For Sale' : 'Lease'}
                    </span>
                  </div>
                  <button 
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`wishlist-vehicle-${vehicle.id}`}
                  >
                    <Heart className="h-4 w-4" />
                  </button>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1">
                    {vehicle.make} {vehicle.model}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" /> {vehicle.year}
                    </span>
                    <span className="flex items-center gap-1">
                      <Gauge className="h-4 w-4" /> {formatMileage(vehicle.mileage)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-primary font-bold text-lg price-tag">
                      {formatCurrency(vehicle.price)}
                      {vehicle.vehicle_type === 'lease' && <span className="text-sm font-normal">/mo</span>}
                    </p>
                    <Button size="sm" className="rounded-full" data-testid={`view-vehicle-${vehicle.id}`}>
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full py-20 text-center text-muted-foreground">
              <Car className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No vehicles found</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
